/**
 * Sprint 4 — 7 Mrt 2026
 * DocumentSetEmailDialog — bevestigingsdialog om de complete documentenset
 * (Boekingsformulier + Paklijst + Waardefactuur) naar de klant te mailen.
 *
 * Belangrijk: deze dialog wijzigt GEEN PDF-inhoud. Bestaande PDF's worden
 * door de backend als bijlage verstuurd.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import {
  Loader2, AlertTriangle, FileText, Package, Receipt, Mail, Info,
} from "lucide-react";

const ICON_BY_KIND = {
  booking_form: FileText,
  packing_list: Package,
  value_invoice: Receipt,
};

const LABEL_BY_KIND = {
  booking_form: "Boekingsformulier",
  packing_list: "Paklijst",
  value_invoice: "Waardefactuur",
};

function isValidEmail(value) {
  if (!value) return false;
  const trimmed = value.trim();
  return trimmed.length >= 3 && /.+@.+\..+/.test(trimmed);
}

function normalizeCcList(raw) {
  return (raw || "")
    .split(/[,;\n]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export default function DocumentSetEmailDialog({
  open,
  onOpenChange,
  bookingId,
  bookingReference,
  defaultRecipient,
  documents = [],   // [{ kind, filename }] — preview van bijlagen
  onSent,
}) {
  const [to, setTo] = useState("");
  const [ccRaw, setCcRaw] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setTo(defaultRecipient || "");
      setCcRaw("");
      setSubject("");
      setMessage("");
      setSending(false);
    }
  }, [open, defaultRecipient]);

  const ccList = useMemo(() => normalizeCcList(ccRaw), [ccRaw]);
  const ccValid = ccList.every(isValidEmail);
  const toValid = isValidEmail(to);
  const canSend = toValid && ccValid && documents.length > 0 && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const payload = {
        to: to.trim(),
        cc: ccList.length ? ccList : null,
        subject: subject.trim() || null,
        message: message.trim() || null,
      };
      const r = await adminApi.post(
        `/admin/booking-forms/${bookingId}/email-document-set`,
        payload,
      );
      const status = r.data?.status;
      if (status === "sent") {
        toast.success(`Documentenset verzonden naar ${r.data?.to || to}`);
      } else if (status === "skipped_no_key") {
        toast.warning(
          "E-mail overgeslagen: Resend API-sleutel ontbreekt op de server.",
        );
      } else {
        toast.warning(
          `E-mail status: ${status || "onbekend"}${
            r.data?.error ? " — " + r.data.error : ""
          }`,
        );
      }
      onOpenChange(false);
      if (typeof onSent === "function") onSent(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg rounded-none"
        data-testid="documentset-email-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-chivo text-[#0A192F]">
            <Mail className="w-5 h-5" />
            Documentenset naar klant e-mailen
          </DialogTitle>
        </DialogHeader>

        <div
          className="border-l-4 border-amber-500 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2"
          data-testid="documentset-email-warning"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Controleer de ontvanger en bijlagen voordat u verzendt. De
            documentenset wordt direct naar de klant gemaild.
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="ds-email-to" className="text-xs">
              Ontvanger
            </Label>
            <Input
              id="ds-email-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-none mt-1"
              data-testid="documentset-email-to"
              placeholder="klant@voorbeeld.nl"
            />
            {to && !toValid && (
              <div className="text-[11px] text-red-600 mt-1">
                Geen geldig e-mailadres.
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="ds-email-cc" className="text-xs">
              CC (optioneel — komma gescheiden)
            </Label>
            <Input
              id="ds-email-cc"
              value={ccRaw}
              onChange={(e) => setCcRaw(e.target.value)}
              className="rounded-none mt-1"
              data-testid="documentset-email-cc"
              placeholder="collega@5999cargo.nl, tweede@voorbeeld.nl"
            />
            {ccRaw && !ccValid && (
              <div className="text-[11px] text-red-600 mt-1">
                Een of meer CC-adressen zijn ongeldig.
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="ds-email-subject" className="text-xs">
              Onderwerp (optioneel)
            </Label>
            <Input
              id="ds-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-none mt-1"
              placeholder={`Uw documentenset van 5999Cargo - ${(bookingReference || "").toString().slice(0, 8).toUpperCase()}`}
              data-testid="documentset-email-subject"
            />
          </div>

          <div>
            <Label htmlFor="ds-email-message" className="text-xs">
              Bericht (optioneel — standaard NL bericht wordt gebruikt)
            </Label>
            <Textarea
              id="ds-email-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="rounded-none mt-1 min-h-[80px]"
              placeholder="Beste klant, in de bijlage vindt u de documentenset..."
              data-testid="documentset-email-message"
            />
          </div>

          <div>
            <div className="text-xs font-bold text-[#0A192F] mb-1 flex items-center gap-1">
              <Info className="w-3.5 h-3.5" /> Bijlagen ({documents.length})
            </div>
            <ul
              className="border border-gray-200 divide-y divide-gray-100"
              data-testid="documentset-email-attachments"
            >
              {documents.map((d) => {
                const Icon = ICON_BY_KIND[d.kind] || FileText;
                const label = LABEL_BY_KIND[d.kind] || d.kind;
                return (
                  <li
                    key={d.kind}
                    className="flex items-center gap-2 p-2 text-xs"
                    data-testid={`documentset-email-attachment-${d.kind}`}
                  >
                    <Icon className="w-4 h-4 text-[#0055FF]" />
                    <span className="font-bold text-[#0A192F]">{label}</span>
                    <span className="text-gray-500 truncate">{d.filename}</span>
                  </li>
                );
              })}
              {documents.length === 0 && (
                <li className="p-2 text-xs text-gray-500 italic">
                  Geen bijlagen beschikbaar.
                </li>
              )}
            </ul>
          </div>

          <div
            className="text-[11px] text-gray-500"
            data-testid="documentset-email-booking-ref"
          >
            Boekingsreferentie: {(bookingReference || bookingId || "")
              .toString()
              .slice(0, 8)
              .toUpperCase()}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none"
            data-testid="documentset-email-cancel"
            disabled={sending}
          >
            Annuleren
          </Button>
          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="documentset-email-send"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <Mail className="w-4 h-4 mr-1" />
            )}
            Documentenset verzenden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
